export type ID = string
export type ItemType = 'model' | 'evaluation' | 'user'
export type EvalName = string
export type ModelName = string

export type Eval = {
  id: ID
  evaluation_id: ID
  evaluatee_id: ID
  evaluation_name: EvalName
  evaluatee_name: ModelName
  datetime_completed?: string
  datetime_started: string
  score: number
  num_answered_correctly: number
  num_questions_answered: number
  median_seconds_per_task?: number
  completed?: boolean
  failed?: boolean
  tasks?: Task[]
  label?: string
  formatted_date?: string
  bin?: number
  report_paid?: number
  price?: number
}

export type DateMap = {
  [key: string]: Eval
}

export type Threshold = {
  title: string
  mean: number
  min?: number
  max?: number
}
export type Evaluations = {
  [key: string]: DateMap
}
export type EvaluationsResult = {
  evals: Evaluations
  thresholds: Threshold[]
}

export type TestTaken = {
  evaluation_id: ID
  name: string
  score: number
}
export type SubscriptionLevel = 'free' | 'pro' | 'enterprise' | 'admin'
export type User = {
  id?: ID
  password?: string
  user_image?: string
  full_name?: string
  user_name: string
  bio?: string
  email_address?: string
  join_date?: string
  subscription_level?: SubscriptionLevel
  display_options?: {[key: string]: boolean}
  human?: boolean
  admin_of?: ID[]
  tests_taken?: TestTaken[]
  credits?: number
}

export type Example = {
  id: string
  question: string
  prompt: string
  response_text: string
  correct_answer: string
  correctness?: number
}

// TODO: Fill in these
export type Modality = string
export type EvaluationType = any
export type Tag = {
  id: ID
  name: string
}
// 'truthful' | 'helpful' | 'harmful' | 'alignment'
export type Cadence = 'once' | 'daily' | 'weekly' | 'every 2 weeks' | 'monthly' | 'quarterly'

export type ModelStats = {
  num_questions_answered?: number
  num_answered_correctly?: number
  median_accuracy?: number
  avg_accuracy?: number
  min_accuracy?: number
  max_accuracy?: number
  min_latency?: number
  max_latency?: number
  avg_latency?: number
  median_latency?: number
  calculation_date: string
}

export type Model = {
  id: ID
  name: ModelName
  owner?: User
  description?: string
  publisher?: string
  architecture?: string
  num_parameters?: number
  modalities?: Modality[]
  score?: number
  average_score?: number
  best_evaluation_session?: Eval
  worst_evaluation_session?: Eval
  best_example?: Example
  worst_example?: Example

  public?: boolean
  public_usable?: boolean
  next_run?: 'now' | Cadence

  request_code?: string
  response_code?: string

  cost_per_input_character_usd?: number
  cost_per_output_character_usd?: number
  cost_per_instance_hour_usd?: number
  max_characters_per_minute?: number
  max_request_per_minute?: number
  max_context_window_characters?: number

  price?: number
  tokens_cost?: number

  statistics?: ModelStats
}

export type EvaluationStats = {
  evaluation_id?: ID
  calculation_date?: string
  question_count?: number
  total_seconds?: number
  average_accuracy?: number
  median_accuracy?: number

  min_model_test_seconds?: number
  min_human_test_seconds?: number
  max_model_test_seconds?: number
  max_human_test_seconds?: number
  median_human_test_seconds?: number
  median_model_test_seconds?: number

  min_human_accuracy?: number
  min_model_accuracy?: number
  max_human_accuracy?: number
  max_model_accuracy?: number
  median_human_accuracy?: number
  median_model_accuracy?: number

  median_human_precision?: number
  median_model_precision?: number
  median_human_recall?: number
  median_model_recall?: number

  num_humans_evaluated?: number
  num_models_evaluated?: number
}
export type EvalCheckError = {
  task_num: number
  errors?: string[]
  warnings?: string[]
}
export type EvaluationCheck = {
  min_questions_to_complete?: number
  num_tasks: number
  errors: EvalCheckError[]
  columns: string[]
}
export type Evaluation = {
  id: ID
  name: EvalName
  description?: string
  owner?: User
  task_types?: EvaluationType[]
  modalities?: Modality[]
  min_questions_to_complete?: number
  public?: boolean
  users?: User[]
  samples?: Example[]
  num_tasks?: number
  creationDate?: string
  tags?: Tag[]
  top_model?: Model
  bottom_model?: Model
  humanBaseline?: Model
  stats?: EvaluationStats
  // Tasks importer fields
  csv_url?: string
  default_task_type?: TaskType
  columns_mapping?: ColumnsConfig

  price?: number
  next_run?: 'now' | Cadence
}

export type AlarmMetric = string
export type TriggerCadence = 'once' | 'daily'
export type Alarm = {
  id: ID
  name: string
  description: string
  metric: AlarmMetric
  threshold: number
  predicted_trigger_date: string
  creationDate: string
  last_trigger_date: string
  trigger_cadence: TriggerCadence
  evaluation?: Evaluation
  evaluation_id?: ID
  models?: Model[]
  owner?: User
  public?: boolean
  subscribed?: boolean
}

export const FREE_RESPONSE_QUESTION = 'FRQ'
export const MULTIPLE_CHOICE_QUESTION = 'MCQ'
export const BOOLEAN_QUESTION = 'bool'

export const REDACTED_COLUMN = 'redacted'
export const TYPE_COLUMN = 'type'
export const QUESTION_COLUMN = 'question'
export const BOOL_CORRECT_COLUMN = 'bool_correct'
export const MCQ_CORRECT_COLUMN = 'mcq_correct'
export const MCQ_INCORRECT_COLUMN = 'mcq_incorrect'
export const FRQ_CORRECT_COLUMN = 'frq_correct'
export const FRQ_INCORRECT_COLUMN = 'frq_incorrect'
export const PARAPHRASE = 'paraphrase'
export const columnTypes = {
  [REDACTED_COLUMN]: 'Redacted',
  [TYPE_COLUMN]: 'Task type',
  [QUESTION_COLUMN]: 'Question',
  [BOOL_CORRECT_COLUMN]: 'Boolean: true/false',
  [MCQ_CORRECT_COLUMN]: 'Multiple choice: correct answer',
  [MCQ_INCORRECT_COLUMN]: 'Multiple choice: incorrect answer',
  [FRQ_CORRECT_COLUMN]: 'Free response: correct answer',
  [FRQ_INCORRECT_COLUMN]: 'Free response: incorrect answer',
  [PARAPHRASE]: 'Paraphrase',
}
export const paraphraseable = [
  QUESTION_COLUMN,
  FRQ_CORRECT_COLUMN,
  FRQ_INCORRECT_COLUMN,
  MCQ_CORRECT_COLUMN,
  MCQ_INCORRECT_COLUMN,
]

export type ColumnType =
  | typeof TYPE_COLUMN
  | typeof QUESTION_COLUMN
  | typeof BOOL_CORRECT_COLUMN
  | typeof FRQ_CORRECT_COLUMN
  | typeof FRQ_INCORRECT_COLUMN
  | typeof MCQ_CORRECT_COLUMN
  | typeof MCQ_INCORRECT_COLUMN
  | typeof PARAPHRASE
  | typeof REDACTED_COLUMN
export type ColumnConfig = {
  columnType?: ColumnType
  paraphrases?: string[]
  paraphraseOf?: string
}
export type ColumnsConfig = {[k: string]: ColumnConfig}

export type Response = {
  id: ID
  evaluation_session_id: ID
  evaluatee_id: ID
  task_id: ID
  correctness?: number
  response_time_in_seconds: number
  parsed_response_text: string
  raw_task_text: string
  // raw_response_text: string
  correct: boolean
  creation_date: string
}

// Free response question | multiple choice questions
export type TaskType =
  | typeof MULTIPLE_CHOICE_QUESTION
  | typeof FREE_RESPONSE_QUESTION
  | typeof BOOLEAN_QUESTION

export type Question = {
  id: ID
  task_id: ID
  text: string
}
export type Answer = {
  id: ID
  task_id: ID
  text: string
  correct?: boolean
}

type BoolQuestion = {
  correct?: boolean
}
type FreeResponseQuestion = {}
type MultipleChoiceQuestion = {
  answers?: Answer[]
}
export type Task = {
  id: ID
  type: TaskType
  evaluation_id?: ID
  question?: string
  questions?: Question[]
  redacted?: boolean
} & FreeResponseQuestion &
  MultipleChoiceQuestion &
  BoolQuestion

export type ModelConnection = {
  id?: ID
  evaluation_id: ID
  evaluatee_id: ID
  name: string
  price: number
  cadence: Cadence
  model?: Model
  evaluation?: Evaluation
}

export type Contact = {
  subject: string
  text?: string
  html?: string
  from?: string
  to: string
}

export type LoginParams = {
  login?: string
  password?: string
  code?: string
  external_auth?: 'google' | 'github'
}
